import { useEffect, useState } from 'react'
import { Viewport } from '@10000/react/src/widgets'
import { getElementDimensions } from '../../../utilities.js'
import { Box, useTooltipSeries } from '../'
import { TapElement } from './'

export const ElementHighlight = ({ order, target, manualTarget, onFinish, nextOnFinish, circle, interactive = false, show = true, ...props }) => {
  const [dimensions, setDimensions] = useState()
  const [finished, setFinished] = useState(false)
  const [delayComplete, setDelayComplete] = useState(false)

  const { next, tip, register, active } = useTooltipSeries()
  register?.(order)

  useEffect(() => {
    if(tip > order || tip === -1) {
      setFinished(false)
      setDelayComplete(false)
    }

    if(tip === order) {
      setTimeout(() => setDelayComplete(true), 700)
    }
  }, [tip, order])

  const open = delayComplete && (manualTarget || target?.current?.checkVisibility?.() || typeof target === 'string') && show

  useEffect(() => {
    if(tip === order) {
      const {
        left = 0,
        top = 0,
        width = 0,
        height = 0,
        radius = 0,
        outline = 0,
        border = 0,
        rotate = 0
      } = typeof target === 'string'
        ? measureElement(document.querySelector(target))
        : target?.current
          ? measureElement(target.current)
          : (manualTarget || {})
      const adjustment = outline + border
      setDimensions({
        x: left - adjustment,
        y: top - adjustment,
        w: width + adjustment * 2,
        h: height + adjustment * 2,
        r: circle ? (width + adjustment) / 2 : radius + adjustment,
        cw: left + width / 2,
        ch: top + height / 2,
        rotate
      })
    }
  }, [target, JSON.stringify(manualTarget), circle, tip, order, show]) // eslint-disable-line
  // Show needed for updated positions from scrolling and observers
  useEffect(() => {
    if(active && open && (tip === order)) {
      const timeout = setTimeout(() => {
        setFinished(true)
        onFinish?.()

        if(nextOnFinish) {
          next()
        }
      }, nextOnFinish ? 2000 : 0)

      return () => clearTimeout(timeout)
    }
  }, [active, open, onFinish, nextOnFinish, order, tip, next])

  const hiddenElementOverlayOpen = active && tip === order && !finished && show
  const elementActive = open && active

  return <>
    <Viewport
      {...(interactive ? dimensions : { x: 0, y: 0, w: 0, h: 0 })}
      active={hiddenElementOverlayOpen}
      background="rgba(0, 0, 0, 0)"
      zIndex={hiddenElementOverlayOpen ? 3000 : -1}
      duration={0}
    />
    <Viewport
      {...dimensions}
      active={elementActive}
      background="rgba(0, 0, 0, 0.5)"
      zIndex={elementActive ? 3001 : -1}
    >
      <Highlight {...dimensions} />
    </Viewport>
    {dimensions && elementActive && <>
      <TapElement {...props} {...{ ...dimensions, rotate: undefined }} zIndex={3002} />
    </>}
  </>
}

const Highlight = ({ x, y, w, h, r }) => (
  <Box
    position="absolute"
    left={x}
    top={y}
    width={w}
    height={h}
    r={r}
    shadow="0 0 15px 15px rgba(255, 255, 255, 0.4)"
    zIndex={3002}
  />
)

function measureElement(element) {
  // the bounding rectangle of a rotated element is larger
  const rotate = element.style.rotate
  const transition = element.style.transition
  element.style.transition = 'none'
  element.style.rotate = 'none'
  const dimensions = getElementDimensions(element)
  element.style.rotate = rotate
  element.style.transition = transition
  return { ...dimensions, rotate }
}